import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Download, Trash2, Calendar, FolderOpen, HardDrive, FileText, Loader2, MessageSquare, X, Volume2, Square, ChevronDown, Lock, Unlock, CreditCard, ExternalLink, Coins, ShieldAlert, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useLibraryCard } from './LibraryCardContext';
import { adminHeaders } from '@/lib/adminAuth';

function isBinaryText(text) {
  if (!text) return false;
  if (text.startsWith('%PDF')) return true;
  var sample = text.slice(0, 200);
  for (var i = 0; i < sample.length; i++) {
    var code = sample.charCodeAt(i);
    if (code < 9 || (code > 13 && code < 32)) return true;
  }
  return false;
}

const TYPE_COLORS = {
  audio: 'text-violet-400',
  video: 'text-rose-400',
  image: 'text-emerald-400',
  document: 'text-cyan-400',
};

const GUMROAD_STORE_URL = 'https://keywebster.gumroad.com/l/mimet';

const DocumentDetailModal = ({ document: doc, backendUrl, apiUrl, open, onClose, onDelete, onAnalyze, onRequestCard, onRequestTokens, onDocUpdated }) => {
  const libraryCard = useLibraryCard();
  const [textContent, setTextContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [accessState, setAccessState] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [showTierMenu, setShowTierMenu] = useState(false);
  const [currentTier, setCurrentTier] = useState(doc.access_tier || 'public');
  const audioRef = useRef(null);
  const _cardId = libraryCard?.card?.id || '';
  const fileUrl = `${backendUrl}/api/uploads/${doc.file_path}${_cardId ? `?card_id=${_cardId}` : ''}`;

  const VOICES = [
    { id: 'nova', label: 'Nova', desc: 'Warm, clear' },
    { id: 'alloy', label: 'Alloy', desc: 'Neutral, balanced' },
    { id: 'echo', label: 'Echo', desc: 'Deep, resonant' },
    { id: 'fable', label: 'Fable', desc: 'Expressive' },
    { id: 'onyx', label: 'Onyx', desc: 'Rich, authoritative' },
    { id: 'shimmer', label: 'Shimmer', desc: 'Light, gentle' },
  ];

  // Check access when modal opens
  useEffect(() => {
    if (!open) { setAccessState(null); return; }
    if (libraryCard?.checkAccess) {
      libraryCard.checkAccess(doc.id).then(result => setAccessState(result));
    } else {
      // No library card context — treat as no card
      setAccessState({ has_access: false, reason: 'no_library_card' });
    }
  }, [open, doc.id, libraryCard?.card?.id]);

  const hasAccess = accessState?.has_access === true;

  const handleSpendToken = async () => {
    if (!libraryCard?.spendToken) return;
    setUnlocking(true);
    try {
      const result = await libraryCard.spendToken(doc.id);
      if (result.success) {
        setAccessState({ has_access: true, reason: result.reason });
        toast.success('Document unlocked!');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to unlock';
      toast.error(msg);
    } finally {
      setUnlocking(false);
    }
  };

  const handleSetTier = async (tier) => {
    try {
      await axios.post(`${apiUrl}/library-card/set-access-tier`,
        { document_id: doc.id, access_tier: tier },
        { headers: adminHeaders() }
      );
      setCurrentTier(tier);
      setShowTierMenu(false);
      toast.success(`Access tier set to "${tier}"`);
      // Re-check access with new tier
      if (libraryCard?.checkAccess) {
        const result = await libraryCard.checkAccess(doc.id);
        setAccessState(result);
      }
      onDocUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access tier');
    }
  };

  const handleReadAloud = async () => {
    if (isReading) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setIsReading(false);
      return;
    }
    setLoadingAudio(true);
    try {
      const response = await axios.post(`${apiUrl}/documents/${doc.id}/read-aloud`, { voice: selectedVoice }, { responseType: 'blob' });
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = () => { setIsReading(false); URL.revokeObjectURL(audioUrl); };
      audio.onerror = () => { setIsReading(false); toast.error('Audio playback failed'); };
      await audio.play();
      setIsReading(true);
    } catch (error) {
      const msg = error.response?.status === 400 ? 'This file type cannot be read aloud' : 'Failed to generate audio';
      toast.error(msg);
    } finally {
      setLoadingAudio(false);
    }
  };

  useEffect(() => {
    if (!open && audioRef.current) { audioRef.current.pause(); audioRef.current = null; setIsReading(false); }
  }, [open]);

  useEffect(() => {
    if (!open || !hasAccess) return;
    setTextContent(null);
    const ext = (doc.file_path || '').split('.').pop().toLowerCase();
    const textExts = ['txt', 'md', 'csv', 'html', 'json', 'xml', 'py', 'js', 'css', 'gdoc', 'gdo', 'gsheet', 'yaml', 'yml', 'yam'];
    const isText = textExts.includes(ext);
    const isPdf = ext === 'pdf';

    if (!isPdf && (doc.type === 'document' || isText)) {
      setLoadingContent(true);
      fetch(fileUrl)
        .then(r => { if (!r.ok) throw new Error('File not on disk'); return r.text(); })
        .then(text => { if (text && !isBinaryText(text)) { setTextContent(text); } else { throw new Error('Binary content'); } })
        .catch(() => {
          fetch(`${apiUrl}/documents/${doc.id}/content?card_id=${libraryCard?.card?.id || ''}`)
            .then(r => r.json())
            .then(data => { if (data.content) setTextContent(data.content); })
            .catch((err) => console.error('Content fallback error:', err));
        })
        .finally(() => setLoadingContent(false));
    }
  }, [open, doc.id, doc.file_path, doc.type, fileUrl, apiUrl, hasAccess]);

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return d; }
  };

  const handleDownload = () => {
    const a = window.document.createElement('a');
    a.href = fileUrl;
    a.download = doc.file_name || doc.title;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  const typeColor = TYPE_COLORS[doc.type] || TYPE_COLORS.document;

  // ── Gated Preview (no access) ──
  const renderGatedView = () => {
    const reason = accessState?.reason || 'no_library_card';
    const tokenBalance = accessState?.token_balance ?? 0;
    const hasTokensNotUnlocked = reason === 'has_tokens_not_unlocked';
    const noCard = reason === 'no_library_card';
    const noTokens = reason === 'no_tokens';
    const isRestricted = reason === 'restricted';

    return (
      <div className="relative" data-testid="gated-content">
        {/* Blurred preview */}
        <div className="relative rounded-xl overflow-hidden">
          <div className="bg-gradient-to-b from-slate-800/40 via-slate-900/60 to-slate-950 p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
            {/* Fake blurred text lines */}
            <div className="w-full max-w-md space-y-3 mb-8 opacity-30 select-none" aria-hidden="true">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-3 bg-slate-600 rounded" style={{ width: `${60 + Math.random() * 40}%`, filter: 'blur(4px)' }} />
              ))}
            </div>

            {/* Lock icon + message */}
            <div className="bg-slate-900/80 border border-slate-700/60 rounded-2xl p-6 max-w-sm backdrop-blur-xl">
              {isRestricted ? (
                <>
                  <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-3" />
                  <h3 className="text-base font-bold text-white mb-1">Restricted Document</h3>
                  <p className="text-sm text-slate-400 mb-2">This document contains sensitive or private content and is not available for public viewing.</p>
                  <p className="text-xs text-slate-600">Admin access only.</p>
                </>
              ) : noCard ? (
                <>
                  <Lock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                  <h3 className="text-base font-bold text-white mb-1">Library Card Required</h3>
                  <p className="text-sm text-slate-400 mb-4">Register your email to get a free library card with 1 complimentary token.</p>
                  <Button
                    onClick={() => onRequestCard?.()}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-sm font-medium gap-2"
                    data-testid="get-library-card-btn"
                  >
                    <CreditCard className="w-4 h-4" /> Get Your Library Card
                  </Button>
                </>
              ) : hasTokensNotUnlocked ? (
                <>
                  <h3 className="text-base font-bold text-white mb-1">Unlock This Document</h3>
                  <p className="text-sm text-slate-400 mb-1">
                    Use 1 token to read the full content.
                  </p>
                  <p className="text-xs text-cyan-400 mb-4">Your balance: {tokenBalance} token{tokenBalance !== 1 ? 's' : ''}</p>
                  <Button
                    onClick={handleSpendToken}
                    disabled={unlocking}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-sm font-medium gap-2"
                    data-testid="spend-token-btn"
                  >
                    {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
                    {unlocking ? 'Unlocking...' : 'Spend 1 Token to Read'}
                  </Button>
                </>
              ) : noTokens ? (
                <>
                  <h3 className="text-base font-bold text-white mb-1">No Tokens Remaining</h3>
                  <p className="text-sm text-slate-400 mb-4">Purchase tokens to unlock this and any other document in the library.</p>
                  <div className="space-y-2">
                    <Button
                      onClick={() => onRequestTokens?.()}
                      className="w-full bg-cyan-600 hover:bg-cyan-500 text-sm font-medium gap-2"
                      data-testid="buy-tokens-btn"
                    >
                      <Coins className="w-4 h-4" /> Buy & Redeem Tokens
                    </Button>
                    <a
                      href={GUMROAD_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full text-xs text-slate-400 hover:text-cyan-400 transition-colors py-2"
                    >
                      <ExternalLink className="w-3 h-3" /> Browse on Gumroad
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-base font-bold text-white mb-1">Access Restricted</h3>
                  <p className="text-sm text-slate-400 mb-4">Register or log in with your library card to access this document.</p>
                  <Button
                    onClick={() => onRequestCard?.()}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-sm font-medium gap-2"
                    data-testid="get-library-card-btn-fallback"
                  >
                    <CreditCard className="w-4 h-4" /> Get Your Library Card
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Summary preview — always visible */}
        {doc.description && (
          <div className="mt-4 bg-slate-900/30 rounded-lg p-4 border border-slate-800/40">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Preview</p>
            <p className="text-sm text-slate-300 leading-relaxed">{doc.description}</p>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    const ext = (doc.file_path || '').split('.').pop().toLowerCase();

    if (doc.type === 'image') {
      return (
        <div className="rounded-lg overflow-hidden bg-slate-900/50 flex justify-center border border-slate-800/40">
          <img src={fileUrl} alt={doc.title} className="max-w-full max-h-[60vh] object-contain"
            onError={e => { e.target.style.display = 'none'; const p = document.createElement('p'); p.style.cssText = 'padding:40px;color:#64748b;text-align:center;font-size:14px'; p.textContent = 'Image file not available on this server.'; e.target.parentNode.appendChild(p); }}
          />
        </div>
      );
    }

    if (doc.type === 'audio') {
      return (
        <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800/40">
          <audio controls className="w-full" data-testid="audio-player">
            <source src={fileUrl} type={doc.mime_type} />
            <p className="text-sm text-slate-500">Audio file not available on this server.</p>
          </audio>
        </div>
      );
    }

    if (doc.type === 'video') {
      return (
        <div className="rounded-lg overflow-hidden bg-slate-900/50 border border-slate-800/40">
          <video controls className="w-full max-h-[60vh]" data-testid="video-player">
            <source src={fileUrl} type={doc.mime_type} />
            <p className="text-sm text-slate-500">Video file not available on this server.</p>
          </video>
        </div>
      );
    }

    if (ext === 'pdf') {
      return (
        <div className="rounded-lg overflow-hidden bg-slate-900/50 border border-slate-800/40">
          <iframe src={fileUrl} title={doc.title} className="w-full h-[70vh]" data-testid="pdf-viewer" />
        </div>
      );
    }

    if (loadingContent) {
      return (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading document content...
        </div>
      );
    }

    if (isBinaryText(textContent)) {
      return (
        <div className="bg-slate-900/30 rounded-lg p-10 text-center border border-slate-800/40">
          <FileText className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <p className="text-sm text-slate-400 mb-4">This is a binary file. Download to view it properly.</p>
          <Button variant="outline" onClick={handleDownload} className="gap-2">
            <Download className="w-4 h-4" /> Download {doc.file_name}
          </Button>
        </div>
      );
    }

    if (textContent) {
      const isMarkdown = ext === 'md';
      return (
        <ScrollArea className="h-[60vh] rounded-lg bg-slate-900/30 border border-slate-800/40">
          <div className="p-6">
            {isMarkdown ? (
              <div className="prose prose-invert prose-sm max-w-none" data-testid="document-content">
                {textContent.split('\n').map((line, i) => {
                  const key = `md-${i}-${line.slice(0, 20)}`;
                  if (line.startsWith('# ')) return <h1 key={key} className="text-xl font-bold text-cyan-400 mt-6 mb-3">{line.slice(2)}</h1>;
                  if (line.startsWith('## ')) return <h2 key={key} className="text-lg font-semibold text-teal-400 mt-5 mb-2">{line.slice(3)}</h2>;
                  if (line.startsWith('### ')) return <h3 key={key} className="text-base font-medium text-slate-200 mt-4 mb-1">{line.slice(4)}</h3>;
                  if (line.startsWith('---')) return <hr key={key} className="border-slate-800 my-4" />;
                  if (line.startsWith('- ') || line.startsWith('* ')) return <li key={key} className="text-sm text-slate-300 ml-4 mb-0.5">{line.slice(2)}</li>;
                  if (line.startsWith('```')) return <div key={key} className="bg-slate-800/60 px-3 py-1 rounded text-xs font-mono text-cyan-300">{line}</div>;
                  if (line.startsWith('**') && line.endsWith('**')) return <p key={key} className="text-sm font-semibold text-slate-200 mb-1">{line.replace(/\*\*/g, '')}</p>;
                  if (line.trim() === '') return <div key={key} className="h-3" />;
                  return <p key={key} className="text-sm text-slate-300 leading-relaxed mb-1">{line}</p>;
                })}
              </div>
            ) : (
              <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono leading-relaxed" data-testid="document-content">
                {textContent}
              </pre>
            )}
          </div>
        </ScrollArea>
      );
    }

    return (
      <div className="bg-slate-900/30 rounded-lg p-10 text-center border border-slate-800/40">
        <FileText className="w-12 h-12 text-slate-700 mx-auto mb-4" />
        <p className="text-sm text-slate-400 mb-2">Content is loading or not available for preview.</p>
        <p className="text-xs text-slate-600 mb-4">Try downloading the file directly.</p>
        <Button variant="outline" onClick={handleDownload} className="gap-2">
          <Download className="w-4 h-4" /> Download {doc.file_name}
        </Button>
      </div>
    );
  };

  if (!open) return null;

  const isChecking = accessState === null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="document-detail-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[92vh] mx-4 bg-slate-950 border border-slate-800/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-800/40">
          <div className="flex-1 pr-4">
            <h2 className="text-lg font-semibold text-white leading-tight mb-2" data-testid="modal-document-title">
              {doc.title}
            </h2>
            <div className="flex gap-2 flex-wrap">
              <Badge className={`capitalize text-[10px] ${typeColor} bg-transparent border border-current/20`}>{doc.type}</Badge>
              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-700">{doc.category}</Badge>
              {doc.is_featured && <Badge className="text-[10px] bg-amber-600/20 text-amber-400 border border-amber-500/30">Featured</Badge>}
              {hasAccess && (
                <Badge className="text-[10px] bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 gap-1">
                  <Unlock className="w-2.5 h-2.5" /> Unlocked
                </Badge>
              )}
              {!hasAccess && !isChecking && accessState?.reason === 'restricted' && (
                <Badge className="text-[10px] bg-red-600/20 text-red-400 border border-red-500/30 gap-1">
                  <ShieldAlert className="w-2.5 h-2.5" /> Restricted
                </Badge>
              )}
              {!hasAccess && !isChecking && accessState?.reason !== 'restricted' && (
                <Badge className="text-[10px] bg-amber-600/20 text-amber-400 border border-amber-500/30 gap-1">
                  <Lock className="w-2.5 h-2.5" /> Token Required
                </Badge>
              )}
              {/* Admin access tier toggle */}
              {libraryCard?.isAdmin && (
                <div className="relative ml-1">
                  <button
                    onClick={() => setShowTierMenu(!showTierMenu)}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
                    data-testid="admin-tier-toggle"
                  >
                    <Shield className="w-2.5 h-2.5" />
                    {currentTier}
                  </button>
                  {showTierMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowTierMenu(false)} />
                      <div className="absolute top-full left-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-20 py-1 min-w-[130px]">
                        {[
                          { tier: 'free', label: 'Free', color: 'text-emerald-400' },
                          { tier: 'paid', label: 'Paid', color: 'text-amber-400' },
                          { tier: 'restricted', label: 'Restricted', color: 'text-red-400' },
                        ].map(t => (
                          <button key={t.tier}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-800 transition-colors ${currentTier === t.tier ? `${t.color} bg-slate-800/50 font-medium` : 'text-slate-300'}`}
                            onClick={() => handleSetTier(t.tier)}
                            data-testid={`set-tier-${t.tier}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isChecking ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-3" /> Checking access...
            </div>
          ) : hasAccess ? (
            renderContent()
          ) : (
            renderGatedView()
          )}

          {hasAccess && doc.description && (
            <p className="text-xs text-slate-500 leading-relaxed">{doc.description}</p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-800/40 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><HardDrive className="w-3.5 h-3.5" />{formatSize(doc.file_size)}</span>
              <span className="flex items-center gap-1.5"><FolderOpen className="w-3.5 h-3.5" />{doc.category}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(doc.created_at)}</span>
            </div>
            <div className="flex gap-2">
              {/* Read Aloud — only for text documents with access */}
              {hasAccess && doc.type === 'document' && (
                <div className="flex items-center gap-0">
                  <Button variant="outline" size="sm" onClick={handleReadAloud} disabled={loadingAudio}
                    className={`gap-1.5 text-xs rounded-r-none ${isReading ? 'text-violet-400 border-violet-800/40 bg-violet-900/20' : 'text-slate-300'}`}
                    data-testid="read-aloud-button">
                    {loadingAudio ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isReading ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    {loadingAudio ? 'Generating...' : isReading ? 'Stop' : 'Read Aloud'}
                  </Button>
                  <div className="relative">
                    <Button variant="outline" size="sm" onClick={() => setShowVoiceMenu(!showVoiceMenu)}
                      className="text-xs px-1.5 rounded-l-none border-l-0" data-testid="voice-select-button">
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                    {showVoiceMenu && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowVoiceMenu(false)} />
                        <div className="absolute bottom-full right-0 mb-1 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-20 py-1 min-w-[160px]">
                          {VOICES.map(v => (
                            <button key={v.id}
                              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-800 transition-colors ${selectedVoice === v.id ? 'text-violet-400 bg-slate-800/50' : 'text-slate-300'}`}
                              onClick={() => { setSelectedVoice(v.id); setShowVoiceMenu(false); }}
                              data-testid={`voice-${v.id}`}>
                              <span className="font-medium">{v.label}</span>
                              <span className="text-slate-500 ml-1.5">{v.desc}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {hasAccess && onAnalyze && (
                <Button variant="outline" size="sm" onClick={() => onAnalyze(doc.id)} className="gap-1.5 text-xs text-cyan-400 border-cyan-800/40 hover:bg-cyan-900/20">
                  <MessageSquare className="w-3.5 h-3.5" /> Analyze
                </Button>
              )}
              {hasAccess && (
                <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5 text-xs text-emerald-400 border-emerald-800/40 hover:bg-emerald-900/20" data-testid="source-document-button">
                  <ExternalLink className="w-3.5 h-3.5" /> Source Document
                </Button>
              )}
              {hasAccess && (
                <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5 text-xs" data-testid="download-button">
                  <Download className="w-3.5 h-3.5" /> Download
                </Button>
              )}
              <Button variant="destructive" size="sm" onClick={() => onDelete(doc.id)} className="gap-1.5 text-xs" data-testid="delete-button">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailModal;
